import { RocketLaunchIcon } from '@heroicons/react/24/outline';
import env from '../../../environment';
import type { FeaturePageDataInterface } from './types';

export const noTechnicalKnowledgeRequired: FeaturePageDataInterface = {
  slug: 'no-technical-knowledge-required',
  navTitle: 'No technical knowledge required',
  shortTitle: 'No code',
  navDescription:
    'Configure crawl targets and extraction schemas without writing scrapers. No code, no proxy farm, no custom pipeline.',
  Icon: RocketLaunchIcon,
  headline: 'Launch web crawls with',
  highlight: 'no technical knowledge required',
  blurb:
    'If you can describe what to collect, you can run a crawl. Set targets, define structured fields, and capture prospects from the open web — all from one dashboard, without building scrapers.',
  quote: {
    text: 'We went from a research brief to an accepted host list and a working schema the same afternoon — nobody wrote a scraper.',
    author: 'Customer name',
    authorRole: 'Job title, Company',
  },
  image: {
    src: '/images/shots/schema-setup.png',
    alt: 'The Stark Crawl dashboard showing an extraction schema configured without code',
  },
  content: [
    {
      type: 'block',
      body: {
        heading: 'Built for the person who owns the research',
        body: [
          'Most crawl stacks assume a developer owns the setup: proxies, parsers, pipelines, and deployment. Stark Crawl assumes the opposite — that the person who needs the market data is the one launching the crawl, not the one who maintains scrapers.',
          'That means a category analyst can stand up a vertical project, a sales ops lead can capture prospects from directories, and a competitor researcher can monitor hosts — without raising a ticket with engineering.',
        ],
        large: true,
      },
    },
    {
      type: 'block',
      body: {
        heading: 'Everything happens in one dashboard',
        body: [
          'Seed directories and hosts, accept discoveries, and define the fields you need — company name, contacts, categories, product attributes — as a schema, not as code.',
          'Runs, coverage, and captures stay visible in the same project. You can refine the schema and re-run without rebuilding a pipeline.',
        ],
      },
    },
    {
      type: 'block',
      body: {
        heading: 'From brief to structured rows',
        body: [
          'When the host set looks right and fields are filling in, export research-ready data for analysis or outreach. Invite teammates to the same project so review and export are shared work, not a laptop ritual.',
          'There is nothing to host and nothing to maintain for each new directory. Change targets or schema, and the project continues from there.',
        ],
      },
    },
    {
      type: 'solution',
      body: 'The whole journey — seed targets, set a schema, crawl, and export — starts in minutes on the free plan. No card required.',
      cta: 'Try for free',
      url: env.appUrl + '/register',
    },
  ],
  seo: {
    title: 'No-code web crawling for market research - Stark Crawl',
    description:
      'Configure crawl targets and extraction schemas without writing scrapers. Discover hosts, extract structured fields, and capture prospects from one dashboard.',
    keywords:
      'no-code web crawl, market research crawl, structured data extraction, prospect capture, Stark Crawl',
  },
  ctaTitle: 'Live in under 10 minutes',
  ctaBody:
    'Seed hosts, define your schema, and start capturing structured research data — no code, no scrapers. Try it free.',
};
